import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { 
  ArrowLeft, 
  CalendarPlus, 
  Plus, 
  Trash2,
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Image,
  Loader2,
  X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const CreateEvent = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const flyerInputRef = useRef(null);
  const permitInputRef = useRef(null);
  const [uploadingFlyer, setUploadingFlyer] = useState(false);
  const [uploadingPermit, setUploadingPermit] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    description: "",
    event_date: "",
    end_date: "",
    venue: "",
    venue_address: "",
    parish: "",
    category: "",
    age_restriction: "",
    layout_type: "general_admission",
    seating_info: "",
    flyer_url: "",
    permit_document_url: "",
    presale_details: "",
    promo_copy: "",
    ticket_tiers: [
      { name: "General Admission", price: 0, quantity: 100, description: "", early_bird: false, early_bird_price: null, early_bird_end_date: "", max_per_order: 10 }
    ]
  });

  const parishes = [
    "Kingston", "St. Andrew", "St. Thomas", "Portland", "St. Mary",
    "St. Ann", "Trelawny", "St. James", "Hanover", "Westmoreland",
    "St. Elizabeth", "Manchester", "Clarendon", "St. Catherine"
  ];

  const categories = [
    "Concert", "Festival", "Party", "Day Party", "Beach Event",
    "Club Night", "Comedy", "Sports", "Cultural", "Other"
  ];

  const createEventMutation = useMutation({
    mutationFn: async (data) => {
      const res = await axios.post(`${API}/events/create`, data);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success("Event created as draft!");
      navigate(`/promoter/events/${data.event.event_id}`);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to create event");
    }
  });

  // File upload handler
  const handleFileUpload = async (file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    
    const endpoint = type === "flyer" ? "/upload/event-flyer" : "/upload/permit";
    const setUploading = type === "flyer" ? setUploadingFlyer : setUploadingPermit;
    const fieldName = type === "flyer" ? "flyer_url" : "permit_document_url";
    
    setUploading(true);
    
    try {
      const res = await axios.post(`${API}${endpoint}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Construct full URL if it's a local path
      let fullUrl = res.data.url;
      if (fullUrl.startsWith("/uploads/")) {
        fullUrl = `${API.replace('/api', '')}${fullUrl}`;
      }
      
      setForm(prev => ({ ...prev, [fieldName]: fullUrl }));
      toast.success(`${type === "flyer" ? "Flyer" : "Permit"} uploaded successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to upload ${type}`);
    } finally {
      setUploading(false);
    }
  };

  const addTier = () => {
    setForm({
      ...form,
      ticket_tiers: [
        ...form.ticket_tiers,
        { name: "", price: 0, quantity: 100, description: "", early_bird: false, early_bird_price: null, early_bird_end_date: "", max_per_order: 10 }
      ]
    });
  };

  const removeTier = (index) => {
    if (form.ticket_tiers.length <= 1) return;
    setForm({
      ...form,
      ticket_tiers: form.ticket_tiers.filter((_, i) => i !== index)
    });
  };

  const updateTier = (index, field, value) => {
    const tiers = [...form.ticket_tiers];
    tiers[index] = { ...tiers[index], [field]: value };
    setForm({ ...form, ticket_tiers: tiers });
  };

  const handleSubmit = () => {
    if (!form.name || !form.event_date || !form.venue || !form.parish || !form.category) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (form.ticket_tiers.some(t => !t.name || t.price < 0 || t.quantity < 1)) {
      toast.error("Please complete all ticket tier information");
      return;
    }

    createEventMutation.mutate(form);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-24">
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate("/promoter")}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Create Event</h1>
            <p className="text-xs text-muted-foreground">Step {step} of 4</p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div 
              key={s} 
              className={`h-1 flex-1 rounded-full transition-all ${
                s <= step ? "bg-pink-500" : "bg-white/10"
              }`}
            />
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="glass-light rounded-xl p-4 space-y-4">
              <h3 className="font-semibold">Event Details</h3>
              
              <div>
                <label className="text-xs text-muted-foreground">Event Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g., Summer Beach Party 2026"
                  required
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Description *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[100px]"
                  placeholder="Describe your event..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Category *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Select</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">Age Restriction</label>
                  <select
                    value={form.age_restriction}
                    onChange={(e) => setForm({ ...form, age_restriction: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">None</option>
                    <option value="18+">18+</option>
                    <option value="21+">21+</option>
                    <option value="All Ages">All Ages</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">End Date & Time</label>
                  <input
                    type="datetime-local"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            <Button onClick={() => setStep(2)} className="w-full btn-primary">
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Location */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="glass-light rounded-xl p-4 space-y-4">
              <h3 className="font-semibold">Location</h3>

              <div>
                <label className="text-xs text-muted-foreground">Venue Name *</label>
                <input
                  type="text"
                  value={form.venue}
                  onChange={(e) => setForm({ ...form, venue: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g., Sabina Park"
                  required
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Venue Address</label>
                <input
                  type="text"
                  value={form.venue_address}
                  onChange={(e) => setForm({ ...form, venue_address: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  placeholder="Full address"
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Parish *</label>
                <select
                  value={form.parish}
                  onChange={(e) => setForm({ ...form, parish: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  required
                >
                  <option value="">Select Parish</option>
                  {parishes.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Layout Type</label>
                <select
                  value={form.layout_type}
                  onChange={(e) => setForm({ ...form, layout_type: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="general_admission">General Admission</option>
                  <option value="seated">Seated</option>
                  <option value="mixed">Mixed (GA + Reserved)</option>
                </select>
              </div>

              {form.layout_type !== "general_admission" && (
                <div>
                  <label className="text-xs text-muted-foreground">Seating Details</label>
                  <textarea
                    value={form.seating_info}
                    onChange={(e) => setForm({ ...form, seating_info: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                    placeholder="Describe seating arrangement..."
                  />
                </div>
              )}
            </div>

            <Button onClick={() => setStep(3)} className="w-full btn-primary">
              Continue
            </Button>
          </div>
        )}

        {/* Step 3: Tickets */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="glass-light rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Ticket Tiers</h3>
                <button 
                  onClick={addTier}
                  className="text-xs text-pink-400 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Tier
                </button>
              </div>

              {form.ticket_tiers.map((tier, index) => (
                <div key={index} className="bg-white/5 rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Tier {index + 1}</span>
                    {form.ticket_tiers.length > 1 && (
                      <button onClick={() => removeTier(index)} className="text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <input
                    type="text"
                    value={tier.name}
                    onChange={(e) => updateTier(index, "name", e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    placeholder="Tier name (e.g., VIP, General)"
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-muted-foreground">Price (J$)</label>
                      <input
                        type="number"
                        value={tier.price}
                        onChange={(e) => updateTier(index, "price", parseFloat(e.target.value) || 0)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground">Quantity</label>
                      <input
                        type="number"
                        value={tier.quantity}
                        onChange={(e) => updateTier(index, "quantity", parseInt(e.target.value) || 0)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={tier.early_bird}
                      onChange={(e) => updateTier(index, "early_bird", e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-xs">Early Bird Pricing</span>
                  </div>

                  {tier.early_bird && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-muted-foreground">Early Bird Price</label>
                        <input
                          type="number"
                          value={tier.early_bird_price || ""}
                          onChange={(e) => updateTier(index, "early_bird_price", parseFloat(e.target.value) || null)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Ends</label>
                        <input
                          type="datetime-local"
                          value={tier.early_bird_end_date}
                          onChange={(e) => updateTier(index, "early_bird_end_date", e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Button onClick={() => setStep(4)} className="w-full btn-primary">
              Continue
            </Button>
          </div>
        )}

        {/* Step 4: Assets & Permit */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="glass-light rounded-xl p-4 space-y-4">
              <h3 className="font-semibold">Marketing Assets</h3>

              {/* Flyer Upload */}
              <div>
                <label className="text-xs text-muted-foreground">Event Flyer</label>
                <input
                  type="file"
                  ref={flyerInputRef}
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "flyer")}
                  className="hidden"
                />
                
                {form.flyer_url ? (
                  <div className="mt-2 relative">
                    <img 
                      src={form.flyer_url} 
                      alt="Flyer preview" 
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => setForm({ ...form, flyer_url: "" })}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-2 left-2 bg-emerald-500/80 text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Uploaded
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => flyerInputRef.current?.click()}
                    disabled={uploadingFlyer}
                    className="w-full mt-2 h-32 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-pink-500/50 transition-all"
                  >
                    {uploadingFlyer ? (
                      <Loader2 className="w-6 h-6 text-pink-400 animate-spin" />
                    ) : (
                      <>
                        <Image className="w-6 h-6 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Click to upload flyer image</span>
                        <span className="text-[10px] text-muted-foreground">PNG, JPG up to 5MB</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Promotional Copy</label>
                <textarea
                  value={form.promo_copy}
                  onChange={(e) => setForm({ ...form, promo_copy: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                  placeholder="Additional promotional text..."
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Presale/Offer Details</label>
                <textarea
                  value={form.presale_details}
                  onChange={(e) => setForm({ ...form, presale_details: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g., Early bird ends Dec 31, Use code NOVA10..."
                />
              </div>
            </div>

            {/* Permit Upload */}
            <div className="glass-light rounded-xl p-4 space-y-4 border-l-4 border-amber-500">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold">Event Permit</h3>
                  <p className="text-xs text-muted-foreground">Required for events in Jamaica</p>
                </div>
              </div>

              <input
                type="file"
                ref={permitInputRef}
                accept="application/pdf,image/*"
                onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "permit")}
                className="hidden"
              />

              {form.permit_document_url ? (
                <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-emerald-400">Permit uploaded</span>
                  </div>
                  <button
                    onClick={() => setForm({ ...form, permit_document_url: "" })}
                    className="text-xs text-muted-foreground hover:text-white"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => permitInputRef.current?.click()}
                  disabled={uploadingPermit}
                  className="w-full h-20 border-2 border-dashed border-amber-500/30 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-amber-500/50 transition-all"
                >
                  {uploadingPermit ? (
                    <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-amber-400" />
                      <span className="text-xs text-muted-foreground">Upload permit (PDF or image)</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <Button 
              onClick={handleSubmit} 
              className="w-full btn-primary"
              disabled={createEventMutation.isPending}
            >
              {createEventMutation.isPending ? "Creating..." : "Create Event"}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Event will be saved as draft. Submit for review to go live.
            </p>
          </div>
        )}
      </div>
      <NavBar active="lifestyle" />
    </div>
  );
};

export default CreateEvent;
